import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { useCurrentMatchday } from '@/hooks/useCurrentMatchday'
import { isCompleted } from '@/lib/fixture-status'
import { useUserSelections } from '@/hooks/useUserSelections'
import { useUserPredictions } from '@/hooks/useUserPredictions'
import { useManagerStandings } from '@/hooks/useManagerStandings'
import { useTeams } from '@/hooks/useTeams'
import { useLeagueConfig, type ScoringRules } from '@/hooks/useLeagueConfig'
import { supabase } from '@/integrations/supabase/client'
import { invokeEdgeFunction } from '@/lib/edge-functions'
import { useSeason } from '@/context/SeasonContext'
import { getTeamLogo, getFormalName } from '@/lib/teams'
import { PointsSummaryBar } from './components/PointsSummaryBar'
import { TddSummaryCard } from './components/TddSummaryCard'
import { CompareSelector } from './components/CompareSelector'
import { PickRow } from './components/PickRow'
import type {
  PickEntry,
  SeasonHalf,
  TddSummary,
  ManagerInfo,
  CompareMode,
  ManagerPickEntry,
} from './types'
import { derivePointsFromScore } from './types'
import type { SeasonSplit } from '@/lib/competitions'

/** Determine season half based on league config splits */
function getSeasonHalfFromConfig(matchday: number, splits: SeasonSplit[] | null, totalMatchdays = 38): SeasonHalf {
  if (!splits || splits.length < 2) {
    // Fallback: split at midpoint of total matchdays
    return matchday <= Math.floor(totalMatchdays / 2) ? 'apertura' : 'clausura'
  }
  const firstSplit = splits[0]
  return matchday <= firstSplit.endMatchday ? 'apertura' : 'clausura'
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function SeasonDivider({ half, splits }: { half: SeasonHalf; splits?: SeasonSplit[] | null }) {
  const firstName = splits?.[0]?.name ?? '1H'
  const secondName = splits?.[1]?.name ?? '2H'
  const firstRange = splits ? `MD ${splits[0]?.startMatchday ?? 1}\u2013${splits[0]?.endMatchday ?? 19}` : 'MD 1\u201319'
  const secondRange = splits?.[1] ? `MD ${splits[1].startMatchday}\u2013${splits[1].endMatchday}` : 'MD 20\u201338'

  const label = half === 'apertura' ? firstName : secondName
  const range = half === 'apertura' ? firstRange : secondRange

  return (
    <div className="flex items-center gap-3 py-2">
      <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
      <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
        {label} <span className="font-normal">{range}</span>
      </span>
      <div className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
    </div>
  )
}

/** Derive W/D/L — prefer stored match_result, fall back to points heuristic
 *  for selections processed before match_result field was added */
function deriveResult(
  matchResult: string | null,
  points: number,
  tddUsed: boolean,
  processed: boolean,
  rules: ScoringRules,
): PickEntry['result'] {
  if (!processed) return null
  // Use stored result when available (set by process-matchday-selections)
  if (matchResult === 'win' || matchResult === 'draw' || matchResult === 'loss') return matchResult
  // Fallback: derive from points_earned + TDD status using league scoring rules
  const multiplier = tddUsed ? 2 : 1
  if (points >= rules.win * multiplier) return 'win'
  if (points >= rules.draw * multiplier) return 'draw'
  return 'loss'
}

export function MyPicksSection() {
  const { user, currentMembership } = useAuth()
  const { season } = useSeason()
  const leagueId = currentMembership?.league_id
  const { config: leagueConfig, seasonSplits, competitionId, scoringRules } = useLeagueConfig()

  const [compareMode, setCompareMode] = useState<CompareMode>('none')
  const [compareUserId, setCompareUserId] = useState<string | null>(null)

  // Data hooks
  const { data: currentMatchday, isLoading: isLoadingMatchday } = useCurrentMatchday(season, competitionId)
  const { selectionData, isLoading: isLoadingSelections } = useUserSelections(user?.id, leagueId, season)
  const { data: predictions = [], isLoading: isLoadingPredictions } = useUserPredictions(user?.id, season, undefined, leagueId)
  const { data: standings = [], isLoading: isLoadingStandings } = useManagerStandings(season)
  const { data: teamsData } = useTeams()

  const teams = teamsData?.teams

  // Fetch comparison manager's selections via edge function (passes leagueId for authorization)
  const { data: comparisonSelections } = useQuery({
    queryKey: ['compare-selections', compareUserId, leagueId, season],
    queryFn: async () => {
      if (!compareUserId || !leagueId) return null
      const { data, error } = await invokeEdgeFunction('get-user-selections', {
        body: { userId: compareUserId, leagueId, season },
      })
      if (error) throw new Error(error.message)
      return data as { selections: Array<{
        id: string; team_name: string; matchday: number; used_double_down: boolean;
        points_earned: number; match_result: string | null; processed: boolean;
        is_forfeit?: boolean | null;
      }> }
    },
    enabled: !!compareUserId && !!leagueId,
    staleTime: 2 * 60 * 1000,
  })

  // Fetch fixtures for the season (opponent + score data + odds for autopick + kickoff time)
  const { data: allFixtures = [] } = useQuery({
    queryKey: ['all-fixtures', season, competitionId],
    enabled: competitionId != null,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('fixtures')
        .select('matchday, home_team, away_team, home_score, away_score, home_win_odds, away_win_odds, status, kickoff_time')
        .eq('season', season)
        .eq('competition_id', competitionId!)
      if (error) throw error
      return data ?? []
    },
    staleTime: 5 * 60 * 1000,
  })

  // Map predictions by matchday for h/a lookup
  const predictionsByMatchday = useMemo(() => {
    const map = new Map<number, typeof predictions[0]>()
    predictions.forEach((pred) => map.set(pred.matchday, pred))
    return map
  }, [predictions])

  // Build match results lookup for COMPLETED fixtures: matchday+teamName → { opponent, scores }
  // Uses formal team names as keys for consistent lookup
  const completedFixturesByTeam = useMemo(() => {
    const map = new Map<string, { opponentName: string; isHome: boolean; homeScore: number; awayScore: number }>()
    allFixtures.filter(f => isCompleted(f.status)).forEach((mr) => {
      const homeKey = getFormalName(mr.home_team)
      const awayKey = getFormalName(mr.away_team)
      // Index by both home and away team for easy lookup
      map.set(`${mr.matchday}:${homeKey}`, {
        opponentName: mr.away_team,
        isHome: true,
        homeScore: mr.home_score,
        awayScore: mr.away_score,
      })
      map.set(`${mr.matchday}:${awayKey}`, {
        opponentName: mr.home_team,
        isHome: false,
        homeScore: mr.home_score,
        awayScore: mr.away_score,
      })
    })
    return map
  }, [allFixtures])

  // Build fixture lookup for ALL fixtures (including pending): matchday+teamName → { opponent, isHome, kickoffTime }
  // Uses formal team names as keys for consistent lookup
  const fixturesByTeam = useMemo(() => {
    const map = new Map<string, { opponentName: string; isHome: boolean; kickoffTime: string | null }>()
    allFixtures.forEach((f) => {
      const homeKey = getFormalName(f.home_team)
      const awayKey = getFormalName(f.away_team)
      map.set(`${f.matchday}:${homeKey}`, {
        opponentName: f.away_team,
        isHome: true,
        kickoffTime: f.kickoff_time,
      })
      map.set(`${f.matchday}:${awayKey}`, {
        opponentName: f.home_team,
        isHome: false,
        kickoffTime: f.kickoff_time,
      })
    })
    return map
  }, [allFixtures])

  // Calculate autopick team for each matchday (HIGHEST odds team = biggest underdog)
  const autopickByMatchday = useMemo(() => {
    const map = new Map<number, string>()

    // Group fixtures by matchday
    const fixturesByMatchday = new Map<number, typeof allFixtures>()
    allFixtures.forEach((f) => {
      const existing = fixturesByMatchday.get(f.matchday) || []
      existing.push(f)
      fixturesByMatchday.set(f.matchday, existing)
    })

    // For each matchday, find the team with HIGHEST odds (biggest underdog)
    fixturesByMatchday.forEach((fixtures, matchday) => {
      let highestOdds = -Infinity
      let autopickTeam: string | null = null

      fixtures.forEach((f) => {
        if (f.home_win_odds != null && f.home_win_odds > highestOdds) {
          highestOdds = f.home_win_odds
          autopickTeam = f.home_team
        }
        if (f.away_win_odds != null && f.away_win_odds > highestOdds) {
          highestOdds = f.away_win_odds
          autopickTeam = f.away_team
        }
      })

      if (autopickTeam) {
        map.set(matchday, autopickTeam)
      }
    })

    return map
  }, [allFixtures])

  // Build pick entries from selections + predictions + match results + fixture info
  const pickEntries: PickEntry[] = useMemo(() => {
    return selectionData.selections.map((sel) => {
      const pred = predictionsByMatchday.get(sel.matchday)
      // Use formal team name for fixture lookups (handles name variations)
      const formalTeamName = getFormalName(sel.team_name)
      const mr = completedFixturesByTeam.get(`${sel.matchday}:${formalTeamName}`)
      const fixture = fixturesByTeam.get(`${sel.matchday}:${formalTeamName}`)
      const points = sel.points_earned ?? 0
      // Use completed fixture for isHome if available, otherwise fall back to fixture lookup or prediction
      const isHome = mr?.isHome ?? fixture?.isHome ?? (pred?.predicted_outcome === 'home' ? true
                   : pred?.predicted_outcome === 'away' ? false : null)
      return {
        matchday: sel.matchday,
        seasonHalf: getSeasonHalfFromConfig(sel.matchday, seasonSplits, leagueConfig.total_matchdays),
        teamName: sel.team_name,
        teamLogo: getTeamLogo(sel.team_name, teams),
        result: deriveResult(sel.match_result, points, sel.used_double_down, sel.processed, scoringRules),
        points,
        basePoints: 0,
        gdBonus: 0,
        redCardPenalty: 0,
        tddUsed: sel.used_double_down,
        isAutopick: !!sel.autopicked_at,
        isForfeit: !!sel.is_forfeit,
        processed: sel.processed,
        isHome,
        // For opponent: use completed fixture if available, otherwise use fixture lookup
        opponentName: mr?.opponentName ?? fixture?.opponentName ?? null,
        score: mr ? {
          picked: isHome ? mr.homeScore : mr.awayScore,
          opponent: isHome ? mr.awayScore : mr.homeScore,
        } : null,
        kickoffTime: fixture?.kickoffTime ?? null,
      }
    })
  }, [selectionData.selections, predictionsByMatchday, completedFixturesByTeam, fixturesByTeam, teams, seasonSplits, leagueConfig.total_matchdays])

  // Sort picks in reverse chronological order
  const sortedPicks = useMemo(
    () => [...pickEntries].sort((a, b) => b.matchday - a.matchday),
    [pickEntries]
  )

  // Calculate points summary from picks
  const summary = useMemo(() => {
    let totalPoints = 0
    let wins = 0
    let draws = 0
    let losses = 0
    for (const pick of pickEntries) {
      totalPoints += pick.points
      if (pick.result === 'win') wins++
      else if (pick.result === 'draw') draws++
      else if (pick.result === 'loss') losses++
    }
    return { totalPoints, record: { wins, draws, losses } }
  }, [pickEntries])

  // Compute DD summary split by season half (with points for tooltips)
  // Uses pickEntries which have derived points from scores when not yet processed
  const { tddSummary, ddPoints } = useMemo(() => {
    const aperturaMDs: number[] = []
    const clausuraMDs: number[] = []
    const aperturaPoints: number[] = []
    const clausuraPoints: number[] = []
    const tddPerHalf = Math.floor(leagueConfig.tdd_limit / 2)

    pickEntries.forEach((pick) => {
      if (pick.tddUsed) {
        // Derive points from score if not processed, using league scoring rules
        const derived = derivePointsFromScore(pick.score, pick.tddUsed, scoringRules)
        const displayPoints = pick.processed ? pick.points : derived.points

        if (getSeasonHalfFromConfig(pick.matchday, seasonSplits, leagueConfig.total_matchdays) === 'apertura') {
          aperturaMDs.push(pick.matchday)
          aperturaPoints.push(displayPoints)
        } else {
          clausuraMDs.push(pick.matchday)
          clausuraPoints.push(displayPoints)
        }
      }
    })

    return {
      tddSummary: {
        apertura: { used: aperturaMDs.length, total: tddPerHalf, matchdays: aperturaMDs },
        clausura: { used: clausuraMDs.length, total: tddPerHalf, matchdays: clausuraMDs },
      },
      ddPoints: {
        apertura: aperturaPoints,
        clausura: clausuraPoints,
      },
    }
  }, [pickEntries, seasonSplits, leagueConfig.tdd_limit, leagueConfig.total_matchdays])

  // Build manager list for comparison dropdown (exclude current user)
  const managers: ManagerInfo[] = useMemo(() => {
    return standings
      .filter((s) => s.user_id !== user?.id)
      .map((s, i) => ({
        id: s.user_id,
        displayName: s.user_name,
        initials: getInitials(s.user_name),
        rank: s.current_rank ?? i + 1,
        points: s.total_points,
      }))
  }, [standings, user?.id])

  // Build comparison picks map by matchday
  const comparisonPicksByMatchday = useMemo(() => {
    const map = new Map<number, ManagerPickEntry>()

    // Mode: compare against another manager
    if (compareUserId && comparisonSelections?.selections) {
      comparisonSelections.selections.forEach((sel) => {
        const points = sel.points_earned ?? 0
        // Use formal team name for fixture lookups (handles name variations like "Nott'm Forest")
        const formalTeamName = getFormalName(sel.team_name)
        const mr = completedFixturesByTeam.get(`${sel.matchday}:${formalTeamName}`)
        const isHome = mr?.isHome ?? null
        map.set(sel.matchday, {
          matchday: sel.matchday,
          teamName: sel.team_name,
          teamLogo: getTeamLogo(sel.team_name, teams),
          result: deriveResult(sel.match_result, points, sel.used_double_down, sel.processed, scoringRules),
          points,
          tddUsed: sel.used_double_down,
          isForfeit: !!sel.is_forfeit,
          isHome,
          opponentName: mr?.opponentName ?? null,
          score: mr ? {
            picked: isHome ? mr.homeScore : mr.awayScore,
            opponent: isHome ? mr.awayScore : mr.homeScore,
          } : null,
        })
      })
      return map
    }

    // Mode: compare against autopick (highest odds team = biggest underdog each matchday)
    if (compareMode === 'autopick') {
      pickEntries.forEach((pick) => {
        const autopickTeam = autopickByMatchday.get(pick.matchday)
        if (!autopickTeam || !pick.processed) return

        // Normalize autopick team name to match completedFixturesByTeam keys
        const autopickTeamKey = getFormalName(autopickTeam)
        const mr = completedFixturesByTeam.get(`${pick.matchday}:${autopickTeamKey}`)
        if (!mr) return

        const autopickIsHome = mr.isHome
        const autopickScore = autopickIsHome ? mr.homeScore : mr.awayScore
        const opponentScore = autopickIsHome ? mr.awayScore : mr.homeScore

        let autopickResult: PickEntry['result'] = null
        if (autopickScore > opponentScore) autopickResult = 'win'
        else if (autopickScore < opponentScore) autopickResult = 'loss'
        else autopickResult = 'draw'

        // Calculate points using league scoring rules (no TDD for autopick)
        const autopickDerived = derivePointsFromScore(
          { picked: autopickScore, opponent: opponentScore },
          false,
          scoringRules,
        )
        const autopickPoints = autopickDerived.points

        map.set(pick.matchday, {
          matchday: pick.matchday,
          teamName: autopickTeam,
          teamLogo: getTeamLogo(autopickTeam, teams),
          result: autopickResult,
          points: autopickPoints,
          tddUsed: false,
          isForfeit: false,
          isHome: autopickIsHome,
          opponentName: mr.opponentName,
          score: {
            picked: autopickScore,
            opponent: opponentScore,
          },
        })
      })
      return map
    }

    return map
  }, [compareMode, compareUserId, comparisonSelections, teams, completedFixturesByTeam, pickEntries, autopickByMatchday])

  // Handle compare mode changes
  function handleCompare(mode: CompareMode) {
    setCompareMode(mode)
    if (mode !== 'none' && mode !== 'autopick') {
      setCompareUserId(mode)
    } else {
      setCompareUserId(null)
    }
  }

  // Group picks by season half for dividers
  function shouldShowDivider(index: number): SeasonHalf | null {
    const pick = sortedPicks[index]
    if (index === 0) return pick.seasonHalf
    const prevPick = sortedPicks[index - 1]
    if (pick.seasonHalf !== prevPick.seasonHalf) return pick.seasonHalf
    return null
  }

  // Loading state
  if (!user) {
    return (
      <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
        <p>Please log in to view your picks.</p>
      </div>
    )
  }

  if (isLoadingSelections || isLoadingPredictions || isLoadingMatchday || isLoadingStandings) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6
                    sm:px-6
                    md:px-0">
      {/* Page header */}
      <div className="mb-5">
        <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
          My Picks
        </h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
          {season} Season &middot; Through MD {currentMatchday ?? '...'}
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-3 mb-5
                      sm:grid-cols-2">
        <PointsSummaryBar
          totalPoints={summary.totalPoints}
          record={summary.record}
        />
        <TddSummaryCard
          tddSummary={tddSummary}
          ddPoints={ddPoints}
          splitNames={[seasonSplits[0]?.name ?? '1H', seasonSplits[1]?.name ?? '2H']}
        />
      </div>

      {/* Compare selector + pick count */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
          {sortedPicks.length} pick{sortedPicks.length !== 1 ? 's' : ''}
        </span>
        <CompareSelector
          mode={compareMode}
          managers={managers}
          onCompare={handleCompare}
        />
      </div>

      {/* Pick history list */}
      {sortedPicks.length === 0 ? (
        <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">
          <p className="text-lg font-medium">No picks yet</p>
          <p className="text-sm mt-1">Your pick history will appear here after you make your first selection.</p>
        </div>
      ) : (
        <div className="space-y-0">
          {sortedPicks.map((pick, index) => {
            const divider = shouldShowDivider(index)
            return (
              <div key={pick.matchday}>
                {divider && <SeasonDivider half={divider} splits={seasonSplits} />}
                <PickRow
                  pick={pick}
                  compareMode={compareMode}
                  comparisonPick={comparisonPicksByMatchday.get(pick.matchday)}
                  scoringRules={scoringRules}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
